import cn from 'classnames';
import { Dispatch, MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { Layout } from 'CorePackage/MainModule/components/Layout';
import {
  ISystemInoperability,
  ISystemInoperabilityStoreProps,
} from 'APIPackage/SystemInoperabilityModule/interfaces/ISystemInoperability';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { sortMapper } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortOrderBySelected } from 'CorePackage/MainModule/common/getSortOrderBySelected';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import {
  adminGetSystemInoperabilityList,
  adminGetSystemInoperabilityListNextPage,
  adminSetSystemInoperabilityListOrder,
} from '../actions/adminAction';
import { initialSystemInoperabilityTableColumns } from '../constants/initialSystemInoperabilityTableColumns';
import {
  getAdminSystemInoperabilityList,
  getAdminSystemInoperabilityListPage,
  getAdminSystemInoperabilityTotalCount,
} from '../selectors/adminSelectors';
import tableStyles from './SystemInoperabilityPage.module.scss';
import { TableWithExternalScroll } from '../../../UIPackage/MainModule/components/TableWithExternalScroll';

const SystemInoperabilityPageWithoutStore = ({
  formRef,
  systemInoperabilityList,
  totalCount,
  loading,
  page,
}: ISystemInoperabilityStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const theadDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, tableStyles.TheadCell);
  const theadReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, tableStyles.TheadCell);
  const tbodyRowClassName = cn(tableStyles.Row, tableStyles.TbodyRow);
  const tbodyDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, tableStyles.TbodyCell);
  const tbodyReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, tableStyles.TbodyCell);
  const [tableColumns, setTableColumns] = useState<ITableColumn<ISystemInoperability>[]>(
    initialSystemInoperabilityTableColumns,
  );
  const push = formRef?.current.mutators.push;
  const isSortAvailable = useRef(true);

  useEffect(() => {
    dispatch(adminGetSystemInoperabilityList());
  }, [dispatch]);

  const handleSortClick = useCallback(
    (e: MouseEvent<HTMLElement>): void => {
      if (isSortAvailable.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { id } = (e.target as any).dataset;
        const newColumnsData = handleSortChange(tableColumns, id as keyof ISystemInoperability);

        setTableColumns(newColumnsData);
        const newOrder = getSortOrderBySelected(newColumnsData, sortMapper);

        dispatch(adminSetSystemInoperabilityListOrder(newOrder));
        dispatch(adminGetSystemInoperabilityList());
      }
    },
    [dispatch, tableColumns],
  );

  useCallback(() => {
    const formRecord: ISystemInoperability = {
      startDate: '',
      endDate: '',
      reason: '',
    };

    push?.('records', formRecord);
  }, [push]);
  const onLoadMore = useCallback(() => dispatch(adminGetSystemInoperabilityListNextPage()), [dispatch]);

  const table = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (page <= 0) {
      return;
    }
    setTimeout(() => {
      table.current?.scrollIntoView(false);
    }, 0);
  }, [page]);

  const handleMainTableScroll = (innerRef?: RefObject<HTMLTableElement>) => () => {
    const thead = innerRef?.current?.querySelector('thead');

    if (thead) {
      thead.scrollLeft = innerRef?.current?.scrollLeft ?? 0;
    }
  };

  const Table = () => (
    <table ref={table} className={tableStyles.Table} onScroll={handleMainTableScroll(table)}>
      <thead className={tableStyles.TableHead}>
        <tr className={tableStyles.Row}>
          {tableColumns.map((row, i) => (
            <th
              onClick={handleSortClick}
              data-id={row.id}
              className={i < 2 ? theadDateCellClassName : theadReasonCellClassName}
              key={i}
            >
              {row.name}
              {getSortIcon(tableColumns, row.id)}
            </th>
          ))}
        </tr>
      </thead>
      {!loading && (
        <tbody>
          {systemInoperabilityList.map((row, i) => (
            <tr className={tbodyRowClassName} key={i}>
              <td className={tbodyDateCellClassName}>
                {formatDate({
                  date: row.startDate ?? '',
                  dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                  parseFormat: DATE_FORMAT_ISO_FNS,
                })}
              </td>
              <td className={tbodyDateCellClassName}>
                {formatDate({
                  date: row.endDate ?? '',
                  dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                  parseFormat: DATE_FORMAT_ISO_FNS,
                })}
              </td>
              <td className={tbodyReasonCellClassName}>{row.reason}</td>
            </tr>
          ))}
        </tbody>
      )}
    </table>
  );

  const TableWithScroll = TableWithExternalScroll(Table);

  return (
    <Layout withFooter>
      <div className={tableStyles.SystemInoperabilityPage}>
        <div className={tableStyles.TableWrapper}>
          <div className={tableStyles.Header}>
            <h1 className={tableStyles.Title}>Периоды неработоспособности системы</h1>
          </div>
          <TableWithScroll />
          {systemInoperabilityList.length < totalCount && (
            <Button type="button" text="Показать еще" color="load" onClick={onLoadMore} />
          )}
        </div>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state: IRootState): ISystemInoperabilityStoreProps => ({
  formRef: null,
  systemInoperabilityList: getAdminSystemInoperabilityList(state),
  showSaveButton: false,
  unsavedRecords: null,
  totalCount: getAdminSystemInoperabilityTotalCount(state),
  loading: getCoreIsLoading(state),
  formElementRef: null,
  page: getAdminSystemInoperabilityListPage(state),
});

export const SystemInoperabilityPage = connect(mapStateToProps)(SystemInoperabilityPageWithoutStore);
